import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import {
  GameSession,
  type MilestoneInfo,
  type SessionStatus,
  buildPerfectReplayInputs,
} from '../game/session';
import type { ReplayOutcome } from '@shared/replay';
import { getBuiltinPulseMapBySlug } from '@shared/builtinPulseMaps';
import { type PulseChart, chartDisplayBpm, modeLabel, stripVariantSuffix } from '@shared/chart';
import { songPageMeta } from '@shared/seo/seoMeta';
import { useDocumentHead } from '../seo/useDocumentHead';
import { useAuth } from '../auth/AuthProvider';
import { useProgression } from '../auth/ProgressionProvider';
import { useAnalytics } from '../analytics/AnalyticsProvider';
import { computeProgressTier } from '@shared/analytics';
import '../components/trophyStrip.css';
import './play.css';
import {
  fetchMapDetail,
  fetchPersonalBest,
  recordPlay,
  submitRun,
  type LeaderboardEntry,
  type MapDetail,
  type PersonalBest,
} from '../apiClient/maps';
import { useRunToken } from '../hooks/useRunToken';
import { subscribeVolumeChanges } from '../game/audio/volumePrefs';
import { preloadAudioFile } from '../game/audio/bufferCache';
import { isFirstTimePlayer, markFirstRunComplete } from '../game/firstRun';
import { markSongCompleted } from '../game/songProgress';
import { saveModePref } from '../game/modePref';
import { modePath } from '../game/modeRouting';
import { isCompetitiveMap, type MapProgression } from '@shared/progression';
import { LoopFinaleOverlay } from './play/LoopFinaleOverlay';
import { LoopSpeedupBanner } from './play/LoopSpeedupBanner';
import { useLoopSpeedupBanner } from './play/useLoopSpeedupBanner';
import { ResultsOverlay } from './play/ResultsOverlay';
import { StartOverlay } from './play/StartOverlay';
import { PlayLoadingOverlay } from './play/PlayLoadingOverlay';
import { usePlayShortcuts } from './play/usePlayShortcuts';
import { PauseOverlay } from './play/PauseOverlay';
import { PlayHeaderActions } from './play/PlayHeaderActions';
import { LeaderboardPanel } from './play/LeaderboardPanel';
import { friendlySubmitError } from './play/playMessages';

function isReplayModeAllowed(): boolean {
  if (import.meta.env.DEV) return true;
  if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('gd-test-uid')) {
    return true;
  }
  return false;
}

export function PlayPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const replayParam = searchParams.get('replay');
  const replayMode = replayParam && isReplayModeAllowed() ? replayParam : null;
  const { user } = useAuth();
  const analytics = useAnalytics();
  const { requestToken, getToken } = useRunToken();

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const sessionRef = useRef<GameSession | null>(null);
  // Re-entrancy guard: prevents two overlapping startSession() calls (e.g.
  // double-tap Retry, R + click) from racing GameSession lifecycle. The
  // second call returns early until the first finishes start() or fails.
  const startInFlightRef = useRef<boolean>(false);
  // Captured when the engine actually starts a run, cleared when the
  // run finishes or is abandoned. Lets the abandon path compute a
  // sensible `duration_ms` for navigated-away-mid-song events.
  const runStartedAtRef = useRef<number | null>(null);
  // Mid-run progression snapshot captured by `onMilestone`. The unmount
  // / beforeunload abandon paths read this to populate the abandon
  // event's progress tier without needing to peek the live session.
  const liveProgressionRef = useRef<{ stars: number; cups: number }>({ stars: 0, cups: 0 });
  const [chart, setChart] = useState<PulseChart | null>(null);
  const [mapDetail, setMapDetail] = useState<MapDetail | null>(null);
  const [chartError, setChartError] = useState<string | null>(null);
  const [status, setStatus] = useState<SessionStatus>('idle');
  const [outcome, setOutcome] = useState<ReplayOutcome | null>(null);
  const [needsTap, setNeedsTap] = useState(true);
  const [submitState, setSubmitState] = useState<
    'idle' | 'submitting' | 'submitted' | 'unranked' | 'skipped' | 'error'
  >('idle');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [personalBest, setPersonalBest] = useState<PersonalBest | null>(null);
  const [offlineMode, setOfflineMode] = useState(false);
  // Leaderboard panel starts open; the player can collapse it to a tab if
  // it's covering useful gameplay area.
  const [leaderboardCollapsed, setLeaderboardCollapsed] = useState(false);
  // Tracks first-time status. Initialised from localStorage on mount and
  // refreshed when a run completes (so a Retry after a successful first run
  // turns off the hint immediately, not just on next page load).
  const [isFirstTime, setIsFirstTime] = useState(() => isFirstTimePlayer());
  // Loop-1 finale overlay state. When the player earns 3 stars at the end
  // of loop 1 the session pauses on `awaiting_next_loop` and we put up a
  // splash → mode-card → countdown sequence before calling
  // session.startNextLoop(). `null` means no overlay is showing.
  const [loopFinale, setLoopFinale] = useState<
    { stage: 'splash' | 'mode_card' } | null
  >(null);
  // Progression freshly earned this run (stars/cups). Populated as
  // milestones fire. Reset at run start so retry shows a clean slate.
  const [runProgression, setRunProgression] = useState<{
    stars: number;
    cups: number;
  }>({ stars: 0, cups: 0 });
  // How many full loops the run cleared. Captured at end-of-run so the
  // results overlay can frame an infinite-mode death after surviving
  // multiple loops as a celebration rather than a flat "Failed".
  const [loopsCleared, setLoopsCleared] = useState(0);
  // Snapshot of best-ever progression *before* the run completed, used
  // by the ResultsOverlay to render "new best!" copy. Captured exactly
  // once when onComplete fires — without the snapshot, applyLocalBest()
  // would mutate the live progression context and instantly overwrite
  // the celebratory caption with the post-run value.
  const [resultsPreviousProgression, setResultsPreviousProgression] =
    useState<MapProgression | null>(null);
  const { forMap, applyLocalBest } = useProgression();

  useEffect(() => {
    if (!slug) return;
    // SPA navigation between two /play/:slug routes reuses the PlayPage
    // component. Tear down any running session and reset overlay state so
    // the new map starts fresh - otherwise the old GameSession keeps
    // rendering / making sound under the new title.
    if (sessionRef.current) {
      void sessionRef.current.destroy();
      sessionRef.current = null;
    }
    setNeedsTap(true);
    setOutcome(null);
    setStatus('idle');
    setSubmitState('idle');
    setSubmitError(null);
    setMapDetail(null);
    setChart(null);
    setChartError(null);
    setLeaderboard([]);
    setPersonalBest(null);
    setRunProgression({ stars: 0, cups: 0 });
    setResultsPreviousProgression(null);
    setLoopFinale(null);
    setLoopsCleared(0);
    // Anyone playing a Pulse map should be treated as a Pulse regular
    // by the TopNav and picker after this session — even if they
    // arrived via a deep link. Persist the mode here so a later visit
    // to /profile or /calibrate keeps surfacing Pulse links.
    saveModePref('pulse');

    let cancelled = false;
    const load = async () => {
      try {
        const detail = await fetchMapDetail(slug);
        if (cancelled) return;
        setMapDetail(detail);
        // Phase 2 routing keeps `/play/:slug` (and `/pulse/play/:slug`) on this
        // page, so a non-pulse chart should never reach us — guard it anyway
        // to keep the discriminated union honest end-to-end.
        if (detail.chart.mode !== 'pulse') {
          setChartError(`Map "${detail.chart.slug}" is a ${detail.chart.mode}-mode chart and cannot be played here.`);
          return;
        }
        setChart(detail.chart);
        setLeaderboard(detail.leaderboard);
        setOfflineMode(false);
        fetchPersonalBest(detail.id)
          .then((pb) => {
            if (!cancelled) setPersonalBest(pb);
          })
          .catch((err) => {
            console.warn('Failed to load personal best:', err);
          });
      } catch (err) {
        if (cancelled) return;
        const builtin = getBuiltinPulseMapBySlug(slug);
        if (builtin) {
          console.warn('Server map fetch failed, falling back to built-in chart:', err);
          setChart(builtin.chart);
          setOfflineMode(true);
          return;
        }
        setChartError(err instanceof Error ? err.message : 'Failed to load map');
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Warm the HTTP cache for the track as soon as the chart is known, so the
  // post-tap AudioContext only has to decode (not fetch). Shrinks the
  // cold-start gap that otherwise shows as a black canvas on slower devices.
  useEffect(() => {
    if (chart) {
      preloadAudioFile(chart.audio.src);
    }
  }, [chart]);

  const onMilestone = useCallback(
    (info: MilestoneInfo) => {
      setRunProgression({
        stars: info.starsEarned,
        cups: info.cupsEarned,
      });
      // Mirror to a ref so the abandon path (which runs synchronously
      // in cleanup / beforeunload) can read the latest progression
      // without waiting for a React state flush.
      liveProgressionRef.current = {
        stars: info.starsEarned,
        cups: info.cupsEarned,
      };
      if (info.pause) {
        setLoopFinale({ stage: 'splash' });
      }
    },
    [],
  );

  const onComplete = useCallback(
    async (o: ReplayOutcome) => {
      setOutcome(o);
      // A cleared run (any mode) graduates the player out of the
      // heaviest in-game teaching hints. Replays aren't the player's
      // own live clear, so they don't count.
      if (o.completed && !replayMode) {
        markSongCompleted();
      }
      // Snapshot what stars/cups the session credits to this run. This
      // reads off the same {@link LoopRunState} that fired the milestone
      // events, so it stays in sync even if onMilestone hasn't flushed
      // its state update yet.
      const earned = sessionRef.current?.getProgressionEarnedThisRun() ?? {
        stars: 0,
        cups: 0,
      };
      setRunProgression(earned);
      const loopsClearedCount = sessionRef.current?.getLoopsCleared() ?? 0;
      setLoopsCleared(loopsClearedCount);

      // Analytics: song_end fires regardless of submit-run success so
      // we capture every real play attempt, including offline runs and
      // submission failures. Replay-mode runs are tagged
      // `replay_mode=true` in props so dashboards can filter them out
      // without losing visibility for e2e tests.
      if (mapDetail && runStartedAtRef.current !== null) {
        const durationMs = Math.max(0, Math.round(performance.now() - runStartedAtRef.current));
        analytics.emit({
          eventType: 'song_end',
          mode: 'pulse',
          mapId: mapDetail.id,
          mapSlug: mapDetail.slug,
          difficulty: chart?.difficulty,
          variant: chart?.variant,
          completed: o.completed,
          progressTier: computeProgressTier(earned.stars, earned.cups),
          durationMs,
          props: {
            score: o.score,
            accuracy: o.accuracy,
            perfects: o.perfects,
            goods: o.goods,
            oks: o.oks ?? 0,
            misses: o.misses,
            max_combo: o.maxCombo,
            death_beat: o.deathBeat,
            death_reason: o.deathReason ?? null,
            stars: earned.stars,
            cups: earned.cups,
            loops_cleared: loopsClearedCount,
            offline_mode: offlineMode,
            replay_mode: replayMode,
          },
        });
      }
      runStartedAtRef.current = null;
      liveProgressionRef.current = { stars: 0, cups: 0 };
      // Freeze the pre-run trophy state for the results overlay caption.
      // applyLocalBest() further down will mutate the live context, so
      // we have to capture this *before* the submit-run promise resolves
      // and the "New best!" caption gets overwritten.
      const beforeBest = mapDetail
        ? forMap(mapDetail.slug, 'pulse')
        : ({ mapSlug: slug ?? '', mode: 'pulse', competitiveEligible: false, stars: 0, cups: 0 } satisfies MapProgression);
      setResultsPreviousProgression(beforeBest);
      // First-time mode goes away once the player has demonstrably "got it" -
      // i.e. they cleared the map OR they landed at least one good/perfect
      // hit. Otherwise a player who dies on beat 0 keeps the training wheels
      // for the next attempt instead of losing them after a confused first try.
      const learned = o.completed || o.perfects > 0 || o.goods > 0;
      if (isFirstTime && learned) {
        markFirstRunComplete();
        setIsFirstTime(false);
      }
      if (!sessionRef.current) return;
      if (!mapDetail) {
        setSubmitState('error');
        setSubmitError(
          offlineMode
            ? "Server unreachable - couldn't save this run. Please reload to retry."
            : "Couldn't save this run.",
        );
        return;
      }
      setSubmitState('submitting');
      setSubmitError(null);
      try {
        const replay = sessionRef.current.getCapturedReplay();
        const result = await submitRun({
          mapId: mapDetail.id,
          outcome: o,
          replay,
          progression: earned,
          timing: sessionRef.current.getTimingReport(),
          runToken: getToken(),
        });
        if (result.skipped === 'no_inputs') {
          setSubmitState('skipped');
        } else if (result.progressionSaved === false) {
          // Run row landed but the progression upsert failed; tell the
          // player so they know to retry rather than wonder why their
          // homepage trophies didn't change.
          setSubmitState('error');
          setSubmitError(
            "Score saved, but we couldn't update your stars/cups. Retry to save your progress.",
          );
        } else {
          setSubmitState(result.serverValidated ? 'submitted' : 'unranked');
          // Only record a play when the run actually contributed something.
          // Skipped runs (no inputs, walked-away "completions") shouldn't
          // bump play counts - that pollutes the social signals on browse
          // and home.
          try {
            await recordPlay(mapDetail.id);
          } catch (err) {
            console.warn('Failed to record play count:', err);
          }
        }
        if (result.progression) {
          // Best-ever progression — server is authoritative, but applying
          // it locally avoids a round-trip flicker on the homepage / topnav.
          applyLocalBest({
            mapSlug: mapDetail.slug,
            mode: 'pulse',
            competitiveEligible: result.progression.competitiveEligible,
            stars: result.progression.stars,
            cups: result.progression.cups,
          });
        }
        try {
          const refreshed = await fetchMapDetail(mapDetail.slug);
          setLeaderboard(refreshed.leaderboard);
          setMapDetail(refreshed);
        } catch (err) {
          console.warn('Failed to refresh leaderboard:', err);
        }
        void result;
      } catch (err) {
        console.error('Failed to submit run:', err);
        setSubmitState('error');
        setSubmitError(friendlySubmitError(err));
      }
    },
    [mapDetail, offlineMode, isFirstTime, applyLocalBest, forMap, slug, analytics, chart, replayMode, getToken],
  );

  const startSession = useCallback(async () => {
    if (!chart || !canvasRef.current) return;
    if (startInFlightRef.current) return;
    startInFlightRef.current = true;
    setNeedsTap(false);
    setOutcome(null);
    setSubmitState('idle');
    setSubmitError(null);
    setLoopFinale(null);
    setRunProgression({ stars: 0, cups: 0 });
    setResultsPreviousProgression(null);
    setLoopsCleared(0);

    try {
      if (sessionRef.current) {
        // Mid-run restart (R while paused / Restart button) drops an alive run on
        // the floor. Emit `song_abandon` for it now so analytics still
        // closes the previous song_start — otherwise the dashboards
        // would see a phantom "uncompleted run" for every voluntary
        // restart. {@link emitAbandonRef} no-ops if no run is alive,
        // so this is safe to call from the start-overlay path too.
        emitAbandonRef.current();
        await sessionRef.current.destroy();
        sessionRef.current = null;
      }

      // Loops only run for "real" map plays. Replay mode is a deterministic
      // verifier that runs the captured-input pipeline through the live
      // engine for a single chart traversal.
      const enableLoops = replayMode === null;

      const session = new GameSession({
        chart,
        canvas: canvasRef.current,
        audioOffsetMs: user?.audioOffsetMs ?? 0,
        replayInputs: replayMode === 'perfect' ? buildPerfectReplayInputs(chart) : undefined,
        onStatusChange: (s) => setStatus(s),
        onComplete: (o) => void onComplete(o),
        onMilestone,
        enableLoops,
        testHook: replayMode !== null,
        firstTime: isFirstTime,
      });
      sessionRef.current = session;
      try {
        await session.start();
        // Mark the run as "started" only after audio is alive — a
        // failed `session.start()` (e.g. blocked AudioContext) shouldn't
        // count as a play attempt.
        runStartedAtRef.current = performance.now();
        liveProgressionRef.current = { stars: 0, cups: 0 };
        if (mapDetail) {
          // Open the server-authoritative run session now that the run is
          // live (fire-and-forget — resolves during lead-in).
          requestToken(mapDetail.id);
          analytics.emit({
            eventType: 'song_start',
            mode: 'pulse',
            mapId: mapDetail.id,
            mapSlug: mapDetail.slug,
            difficulty: chart.difficulty,
            variant: chart.variant,
            props: {
              first_time: isFirstTime,
              offline_mode: offlineMode,
              bpm: chart.bpm,
              replay_mode: replayMode,
            },
          });
        }
      } catch (err) {
        console.error('Failed to start game session:', err);
        sessionRef.current = null;
        void session.destroy();
        setNeedsTap(true);
        setSubmitState('error');
        setSubmitError(
          "Couldn't start audio. Try clicking Start again or check your browser's audio permissions.",
        );
      }
    } finally {
      startInFlightRef.current = false;
    }
  }, [chart, mapDetail, offlineMode, replayMode, user?.audioOffsetMs, onComplete, onMilestone, isFirstTime, analytics, requestToken]);

  // Any keypress / click on the start overlay starts the run. The Start button
  // is the obvious target but Space / Enter / a tap on the canvas will all work.
  // Browsers require the gesture to come from inside an event handler, so all
  // these paths funnel through startSession() which handles AudioContext setup.
  useEffect(() => {
    if (!needsTap || !chart) return;
    if (replayMode) return; // replay mode auto-starts via the other useEffect
    const onKey = (e: KeyboardEvent) => {
      if (
        e.key === ' ' ||
        e.code === 'Space' ||
        e.key === 'Enter' ||
        e.code === 'KeyW' ||
        e.code === 'ArrowUp'
      ) {
        e.preventDefault();
        void startSession();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [needsTap, chart, replayMode, startSession]);

  usePlayShortcuts({
    status,
    hasOutcome: outcome !== null,
    onPause: () => sessionRef.current?.manualPause(),
    onResume: () => void sessionRef.current?.resume(),
    onRestart: () => void startSession(),
  });

  // Mid-run live volume: the TopNav speaker popover publishes through the
  // shared pub/sub in volumePrefs.ts. We forward each change into the live
  // audio engine so dragging the slider takes effect inside the same frame.
  useEffect(() => {
    return subscribeVolumeChanges(() => {
      sessionRef.current?.applyVolumePrefs();
    });
  }, []);

  // Emit `song_abandon` if the run was alive at unmount or page-unload.
  // Captures the "started a song then ragequit / closed the tab" cohort
  // that `onComplete` never sees, so the dashboard's completion rate
  // and progress histogram aren't biased toward players who saw it
  // through to the end.
  //
  // Keeping the function in a ref instead of a `useCallback` is
  // deliberate: callbacks rebuild whenever a dep changes (mapDetail,
  // chart, …), which would mean the unmount cleanup below runs on
  // every dep flip and either re-fires the abandon or fires it with a
  // stale closure. The ref pattern guarantees a single emit on real
  // unmount, with the latest deps via the captured closure.
  const emitAbandonRef = useRef(() => {});
  emitAbandonRef.current = () => {
    if (runStartedAtRef.current === null) {
      return;
    }
    if (!mapDetail) {
      runStartedAtRef.current = null;
      return;
    }
    const durationMs = Math.max(0, Math.round(performance.now() - runStartedAtRef.current));
    const earned = liveProgressionRef.current;
    const loopsClearedCount = sessionRef.current?.getLoopsCleared() ?? 0;
    analytics.emit({
      eventType: 'song_abandon',
      mode: 'pulse',
      mapId: mapDetail.id,
      mapSlug: mapDetail.slug,
      difficulty: chart?.difficulty,
      variant: chart?.variant,
      completed: false,
      progressTier: computeProgressTier(earned.stars, earned.cups),
      durationMs,
      props: {
        stars: earned.stars,
        cups: earned.cups,
        loops_cleared: loopsClearedCount,
        offline_mode: offlineMode,
        replay_mode: replayMode,
      },
    });
    // Drain immediately so an unload abandon survives the page going
    // away — the provider's listener may have already drained before
    // this emit landed in the queue.
    void analytics.flushAll('unload');
    runStartedAtRef.current = null;
    liveProgressionRef.current = { stars: 0, cups: 0 };
  };

  useEffect(() => {
    const onUnload = () => emitAbandonRef.current();
    window.addEventListener('beforeunload', onUnload);
    window.addEventListener('pagehide', onUnload);
    return () => {
      window.removeEventListener('beforeunload', onUnload);
      window.removeEventListener('pagehide', onUnload);
    };
  }, []);

  useEffect(() => {
    return () => {
      emitAbandonRef.current();
      if (sessionRef.current) {
        void sessionRef.current.destroy();
        sessionRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (replayMode && chart && needsTap) {
      void startSession();
    }
  }, [replayMode, chart, needsTap, startSession]);

  // Loop-1 finale orchestration: splash → mode card → startNextLoop().
  // Total duration ≈ 3.0s; the canvas's own 4-beat lead-in countdown
  // takes over once we kick startNextLoop(). We unmount the overlay
  // when status flips back to 'lead_in' so the canvas count is visible.
  useEffect(() => {
    if (!loopFinale) {
      return undefined;
    }
    if (loopFinale.stage === 'splash') {
      const t = setTimeout(() => {
        setLoopFinale((cur) => (cur ? { ...cur, stage: 'mode_card' } : cur));
      }, 1800);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => {
      void sessionRef.current?.startNextLoop();
    }, 1200);
    return () => clearTimeout(t);
  }, [loopFinale]);

  // Once the next loop's lead-in begins drawing on canvas, drop the
  // overlay so the player sees the in-engine countdown numbers.
  useEffect(() => {
    if (status === 'lead_in' || status === 'running') {
      setLoopFinale(null);
    }
  }, [status]);

  // Speed-up banner over the count-in for infinite-mode repeats (loop 3+).
  const speedupBanner = useLoopSpeedupBanner(
    status === 'lead_in',
    () => sessionRef.current?.getLoopIndex() ?? 0,
  );

  // Sync the document head to the loaded song for SPA navigation. RouteHead
  // owns the non-play routes but defers play pages to here, where the chart
  // (and so the real song title) is known. No-ops until the chart loads.
  useDocumentHead(
    chart && slug
      ? songPageMeta({
          mode: 'pulse',
          modeLabel: modeLabel('pulse'),
          slug,
          songTitle: stripVariantSuffix(chart),
          songDescription: chart.description,
          bpm: Math.round(chartDisplayBpm(chart.bpm)),
        })
      : null,
  );

  if (chartError) {
    return (
      <div className="play-error">
        <h2>Map not found</h2>
        <p>{chartError}</p>
        <Link to={modePath('pulse')} className="btn">
          Back to Pulse
        </Link>
      </div>
    );
  }

  if (!chart) {
    return (
      <div className="loading-screen">
        <p className="muted">Loading {slug}...</p>
      </div>
    );
  }

  return (
    <div className="play-page">
      <div className="play-header">
        <div>
          {/*
            Builtin chart titles for hard/extreme already embed the variant
            suffix (e.g. "Invincible — Hard") so they're identifiable in
            logs / admin tools / lobby lists. On the play page we strip the
            suffix and render a coloured badge instead — far more readable
            than a long em-dashed title and keeps the variant unmistakable
            without taking up a whole line.
          */}
          <h2 className="play-title">
            <span className="play-title-text">{stripVariantSuffix(chart)}</span>
            {chart.variant !== 'normal' && (
              <span
                className={`variant-badge variant-badge-${chart.variant}`}
                data-testid="play-variant-badge"
              >
                {chart.variant.toUpperCase()}
              </span>
            )}
          </h2>
          <p className="muted">
            {chart.bpm.toFixed(0)} BPM &middot; Difficulty {chart.difficulty} &middot;{' '}
            {chart.pads.filter((p) => !p.optional).length} flips
            {mapDetail?.likeCount !== undefined && (
              <>
                {' '}&middot; {mapDetail.likeCount.toLocaleString()} likes &middot;{' '}
                {mapDetail.playCount.toLocaleString()} plays
              </>
            )}
          </p>
          {chart.audio.attribution && (
            <p className="muted faint play-attribution" data-testid="audio-attribution">
              {chart.audio.attribution}
            </p>
          )}
          {personalBest && personalBest.score > 0 && (
            <p className="play-pb-line muted faint" data-testid="personal-best">
              Your best: <strong>{personalBest.score.toLocaleString()}</strong> &middot;{' '}
              {(personalBest.accuracy * 100).toFixed(0)}% &middot;{' '}
              {personalBest.completed ? 'cleared' : 'incomplete'}
            </p>
          )}
        </div>
        <PlayHeaderActions
          isPlaying={status === 'running' || status === 'lead_in'}
          onPause={() => sessionRef.current?.manualPause()}
          onRestart={() => void startSession()}
          testIdPrefix="play"
        />
      </div>

      {offlineMode && (
        <div className="error-box" data-testid="offline-banner">
          Server unreachable. Playing offline - your run won&apos;t be saved.
        </div>
      )}

      <div className="play-layout">
        <div className="play-canvas-wrap">
          <canvas
            ref={canvasRef}
            className="play-canvas"
            data-testid="game-canvas"
            width={960}
            height={540}
            onClick={() => {
              if (needsTap) void startSession();
            }}
          />

          {needsTap && (
            <StartOverlay
              title={chart.title}
              description={chart.description}
              attribution={
                chart.audio.attribution
                  ? `Music: ${chart.audio.attribution}`
                  : undefined
              }
              instructionsHint={
                <>
                  Press <kbd>Space</kbd> or tap to flip on each pad.
                </>
              }
              isFirstTime={isFirstTime}
              firstTimeHint={
                <>
                  Extra-long count-in. Watch for the &quot;TAP SPACE&quot; arrow on the first pad.
                </>
              }
              onStart={() => void startSession()}
            />
          )}

          {status === 'starting' && !needsTap && (sessionRef.current?.getLoopIndex() ?? 0) === 0 && (
            <PlayLoadingOverlay audioSrc={chart.audio.src} />
          )}

          {status === 'paused' && (
            <PauseOverlay onRestart={() => void startSession()} onResume={() => void sessionRef.current?.resume()} />
          )}

          {loopFinale && <LoopFinaleOverlay stage={loopFinale.stage} />}

          {speedupBanner && (
            <LoopSpeedupBanner
              speed={speedupBanner.speed}
              message={speedupBanner.message}
            />
          )}

          {outcome && (
            <ResultsOverlay
              competitiveEligible={mapDetail !== null && isCompetitiveMap(mapDetail)}
              outcome={outcome}
              submitState={submitState}
              submitError={submitError}
              onRetry={() => void startSession()}
              previousBest={personalBest?.score ?? 0}
              earnedThisRun={runProgression}
              loopsCleared={loopsCleared}
              previousProgression={
                resultsPreviousProgression ?? {
                  mapSlug: slug ?? '',
                  mode: 'pulse',
                  competitiveEligible: false,
                  stars: 0,
                  cups: 0,
                }
              }
              chart={chart}
            />
          )}

          <LeaderboardPanel
            competitiveEligible={mapDetail !== null && isCompetitiveMap(mapDetail)}
            entries={leaderboard}
            collapsed={leaderboardCollapsed}
            onToggle={() => setLeaderboardCollapsed((v) => !v)}
            selfUid={user?.uid}
          />
        </div>
      </div>
    </div>
  );
}
